import {type ReactNode, useEffect, useRef, useState} from "react";
import {Box} from "@mui/material";

interface ResizableSplitProps {
    /** Axis of split. "horizontal" = side-by-side panes, "vertical" = stacked panes. */
    direction?: "horizontal" | "vertical";
    /** Initial ratio of the first pane (0..1). */
    defaultRatio?: number;
    /** Minimum ratio for the first pane. */
    minRatio?: number;
    /** Maximum ratio for the first pane. */
    maxRatio?: number;
    /** Gutter (px) between the handle and the two panes. */
    handleGap?: number;
    first: ReactNode;
    second: ReactNode;
}

function ResizableSplit({
    direction = "horizontal",
    defaultRatio = 0.5,
    minRatio = 0.2,
    maxRatio = 0.8,
    handleGap = 4,
    first,
    second,
}: ResizableSplitProps) {
    const [ratio, setRatio] = useState(defaultRatio);
    const [dragging, setDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const isHorizontal = direction === "horizontal";

    useEffect(() => {
        if (!dragging) return;

        const onMove = (e: MouseEvent) => {
            const el = containerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const size = isHorizontal ? rect.width : rect.height;
            if (size <= 0) return;
            const offset = isHorizontal ? e.clientX - rect.left : e.clientY - rect.top;
            const next = offset / size;
            setRatio(Math.min(maxRatio, Math.max(minRatio, next)));
        };
        const onUp = () => setDragging(false);

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        const prevCursor = document.body.style.cursor;
        const prevSelect = document.body.style.userSelect;
        document.body.style.cursor = isHorizontal ? "col-resize" : "row-resize";
        document.body.style.userSelect = "none";

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            document.body.style.cursor = prevCursor;
            document.body.style.userSelect = prevSelect;
        };
    }, [dragging, isHorizontal, minRatio, maxRatio]);

    const paneSx = {
        flexShrink: 1,
        flexBasis: 0,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column" as const,
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flexDirection: isHorizontal ? "row" : "column",
            }}
        >
            <Box sx={{...paneSx, flexGrow: ratio}}>{first}</Box>

            <Box
                onMouseDown={(e) => {
                    e.preventDefault();
                    setDragging(true);
                }}
                sx={{
                    flexShrink: 0,
                    ...(isHorizontal
                        ? {width: "8px", margin: `0 ${handleGap}px`, cursor: "col-resize"}
                        : {height: "8px", margin: `${handleGap}px 0`, cursor: "row-resize"}),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "4px",
                    background: dragging ? "rgba(2, 207, 238, 0.20)" : "transparent",
                    transition: "background 0.15s",
                    "&:hover": {background: "rgba(2, 207, 238, 0.15)"},
                }}
            >
                <Box sx={{
                    ...(isHorizontal ? {width: "3px", height: "48px"} : {height: "3px", width: "48px"}),
                    borderRadius: "2px",
                    background: dragging ? "#02cfee" : "#3F464F",
                    transition: "background 0.15s",
                }}/>
            </Box>

            <Box sx={{...paneSx, flexGrow: 1 - ratio}}>{second}</Box>
        </Box>
    );
}

export default ResizableSplit;
